<template lang="pug">
.media(v-if="edit" :class="{ 'is-dragging': dragging }" ref="container")
    el-form-item(:label="dropbox ? $t(config.name) : ''")
        draggable(
            v-if="filteredContents && filteredContents.length"
            v-model="contents"
            :group="`${section.id}:media`"
            draggable="li.content"
            handle=".drag-handle"
            @start="dragging = true"
            @end="dragging = false"
        )
            //- if media is a standalone content element
            transition-group(
                name="fade"
                tag="ul"
                class="no-style-list"
                v-if="filteredContents.length && (!content)"
            )
                media-preview(
                    v-for="content in contents" :key="content.id"
                    v-if="!skip(content)"
                    :class="{ edit, deleted: content.deleted, 'is-sortable': filteredContents.length > 1 }"
                    :media="getDisplayMedia(content)"
                    :sortable="filteredContents.length > 1"
                    :height="64"
                    :width="61"
                )
                    el-button.restore-media(
                        v-if="content.deleted"
                        circle type="info" size="mini"
                        icon="el-icon-fa-trash-restore fas"
                        @click.prevent.stop="restoreContent(content.id)"
                    )
                    el-button.delete-media(
                        v-else-if="isDeletable"
                        circle type="danger" size="mini"
                        icon="el-icon-fa-trash-alt far"
                        @click.prevent.stop="deleteContent(content.id)"
                    )

        .media-sort-hint(v-if="filteredContents.length > 1")
            i.el-icon-d-caret
            span {{ $t('Drag and drop to reorder files') }}
        .dropzone-block
            .media-actions(
                v-if="showLibraryAction"
                :class="{ 'media-actions--replace': libraryActionMode === 'replace' }"
            )
                el-button.media-library-button(
                    v-if="libraryActionMode !== 'replace'"
                    type="primary"
                    size="medium"
                    icon="el-icon-document"
                    @click="openLibrary(libraryActionMode)"
                ) {{ openLibraryLabel }}
                el-button.media-library-button.media-library-button--replace(
                    v-else
                    plain
                    size="small"
                    icon="el-icon-document"
                    @click="openLibrary(libraryActionMode)"
                ) {{ openLibraryLabel }}
                .media-actions__hint(v-if="libraryActionMode !== 'replace'") {{ openLibraryHint }}
            el-upload.has-info(
                v-if="! max || mediaCount < max"
                ref="upload"
                :class="dropbox ? 'plus-box' : 'button-upload'"
                :drag="dropbox" multiple :action="uploadEndpoint"
                :accept="allowedMimeTypes"
                :before-upload="validateUpload"
                :on-change="upoloadChanged"
                :on-success="uploadSucceeded"
                :on-error="uploadFailed"
                :headers="uploadHeaders"
                :data="uploadData"
                :limit="! max ? null : max"
            )
                template(v-if="dropbox")
                    i.el-icon-plus
                    // i.el-icon-upload.el-icon-fa-cloud-upload-alt.fas
                    .el-upload__text(v-html=" ! max || max > 1 ? $t('upload.drop.plural') : $t('upload.drop.singular')")
                el-button(v-else size="small" type="primary") {{ $t(config.name) }}
                .el-upload__tip(slot="tip" v-if="allowedSuffixes") {{ $t('upload.validation', { filetypes: allowedSuffixes, size: `${maxSize}mb`}) }}

    image-library-dialog(
        :visible.sync="libraryModal.visibility"
        :multiple="libraryAllowsMultiple"
        :selection-limit="librarySelectionLimit"
        :accepted-mime-types="mimeTypes"
        selection-type="file"
        @select="selectMediaFromLibrary"
    )

.media.preview(v-else ref="container")
    ul.no-style-list(v-if="filteredContents && filteredContents.length")
        //- template(v-if="c === undefined || c === null || c === false")
        template(v-for="content in filteredContents")
            media-preview(
                v-for="media in getValidMedia(content)"
                :class="{ edit, deleted: content.deleted }"
                :key="media.id"
                :media="media"
                :height="42"
                :width="42"
            )
</template>

<i18n>
{
    "de": {
        "Could not find the content.": "Der Inhalt konnte nicht gefunden werden.",
        "Image settings": "Media-Einstellungen",
        "No media found.": "Es konnten keine Medien gefunden werden",
        "Select file from media library": "Datei aus Mediathek auswählen",
        "Select files from media library": "Dateien aus Mediathek auswählen",
        "Replace file": "Datei austauschen",
        "Select an existing file from the media library or upload a new one below.": "Eine vorhandene Datei aus der Mediathek auswählen oder unten eine neue hochladen.",
        "Select existing files from the media library or upload new ones below.": "Vorhandene Dateien aus der Mediathek auswählen oder unten neue hochladen.",
        "Replace the current file with one from the media library.": "Die aktuelle Datei durch eine aus der Mediathek ersetzen.",
        "Drag and drop to reorder files": "Dateien per Drag and Drop sortieren"
    }
}
</i18n>

<script>

import _ from 'lodash'
import mime from 'mime'
import uniqid from 'uniqid'

import draggable from 'vuedraggable'
import { useAuthStore, useSetupStore } from '@/stores'

import store from '@/store'
import AuthApi from '@/store/api/system/auth'

import {
    set,
    ensureFreshToken,
    addContentToSection,
    sortContents,
} from '@/helpers'

import VueCropper from 'vue-cropperjs'
// import ImageField from './ImageContent/ImageField'
import ImageLibraryDialog from './ImageLibraryDialog'
import MediaPreview from './MediaPreview'
import InputField from './InputField'

export default {
    name: 'MediaContent',
    props: {
        section: {
            type: Object,
            required: false
        },
        content: {
            type: Object,
            required: false
        },
        config: {
            type: Object,
            required: true
        },
        dropbox: {
            type: Boolean,
            default: true
        },
        edit: {
            type: Boolean,
            default: false,
        },
        // height: {
        //     type: Object,
        //     default: () => {
        //         return {
        //             preview: 80,
        //             edit: 150,
        //         }
        //     }
        // },

        // all setting values as associative object (key: value)
        sectionSettingValues: {
            type: Object,
            default: () => {
                return {}
            }
        },
        sectionSettings: {
            type: Array,
            required: false
        },
    },
    data() {
        return {
            // maxWidth: undefined,

            // visibleSettings: false,

            libraryModal: {
                visibility: false,
                mode: 'add',
            },

            dragging: false,
        }
    },
    components: {
        // ImageField,
        ImageLibraryDialog,
        MediaPreview,
        InputField,
        VueCropper,
        draggable
    },
    computed: {
        uploadHeaders() {
            return {
                authorization: `Bearer ${useAuthStore().token}`,
                accept: 'application/json',
            }
        },
        id() {
            return _.get(this.form, 'id', false)
        },
        randomId() {
            return uniqid()
        },
        // edit() {
        //     if (this.forceEdit === true) {
        //         return true
        //     } else if (this.module === 'menu' && this.i >= 0) {
        //         return _.get(this.ui.form, `menuItems.${this.i}.page.sections.${this.s}.edit`, false)
        //     } else if (this.module === 'categoryPage') {
        //         return _.get(this.ui.form, `sections.${this.s}.edit`, false)
        //     }
        // },
        isDeletable() {
            if (!this.content) {
                const contents = this.filteredContents.filter(el => ! el.deleted)
                return this.min === null || this.min < contents.length
            } else {
                const media = this.content.media.filter(el => ! el.deleted)
                return this.min === null || this.min < media.length
            }
        },
        contents: {
            get() {
                const contents = _.get(this.section, 'contents') || []

                return contents

                    // NOTE: sort creates infinite loop
                    // .sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0))

                    // NOTE: filter breaks the draggable list
            },
            set(sorted) {
                // sort
                if (!this.content) {
                    let contents = _.get(this.section, 'contents') || []
                    contents.forEach(content => {
                        const i = sorted.findIndex(el => el.id === content.id)
                        content.position = i + 1
                    });
                    sortContents(contents)
                } else {
                    // TODO: sort content.media ?
                }
            }
        },
        filteredContents() {
            return _.cloneDeep(this.contents)
                .filter(content => {
                    return content.key === this.config.key && content.media
                        && this.getValidMedia(content).length > 0
                })
        },
        actualFilteredContents() {
            return (this.contents || [])
                .filter(content => {
                    return content.key === this.config.key && content.media
                        && this.getValidMedia(content).length > 0
                })
        },
        min() {
            let min
            if (!this.content) min = _.get(this.config, 'min')
            else min = _.get(this.config, 'media.min')
            return min
        },
        max() {
            let max
            if (!this.content) {
                max = _.get(this.config, 'max')
                // For isSingleAssetConfig sections (e.g. downloads), config.max may be null
                // while config.media.max carries the actual limit (1). Fall back to it so
                // canReplaceFromLibrary is computed correctly.
                if (max === null || max === undefined) {
                    max = _.get(this.config, 'media.max')
                }
            } else {
                max = _.get(this.config, 'media.max')
            }
            return max
        },
        mimeTypes() {
            return _.get(this.config, 'media.mimeTypes') || []
        },
        allowedMimeTypes() {
            return this.mimeTypes.join(', ')
        },
        allowedSuffixes() {
            if (!this.mimeTypes.length) return false
            return _.compact(_.uniq(this.mimeTypes.map(value => mime.getExtension(value)))).join('/')
        },
        mediaCount() {
            if (this.content) {
                return (_.get(this.content, 'media', []) || []).filter(media => !media.deleted).length
            }

            // Use actualFilteredContents when available, but fall back to a key-only match
            // so that API-loaded content that doesn't pass isValidMediaObject is still counted.
            const validCount = this.actualFilteredContents.filter(content => !content.deleted).length
            if (validCount > 0) return validCount

            return (this.contents || []).filter(content =>
                content.key === this.config.key &&
                content.media && content.media.length > 0 &&
                !content.deleted
            ).length
        },
        librarySelectionLimit() {
            if (this.libraryModal.mode !== 'add') return 1
            if (!this.max) return null

            return Math.max(Number(this.max) - this.mediaCount, 0)
        },
        libraryAllowsMultiple() {
            return this.libraryModal.mode === 'add'
                && (this.librarySelectionLimit === null || this.librarySelectionLimit > 1)
        },
        canReplaceFromLibrary() {
            return this.max !== null
                && this.max !== undefined
                && Number(this.max) === 1
                && this.mediaCount === 1
        },
        showLibraryAction() {
            return this.canReplaceFromLibrary || !this.max || this.mediaCount < Number(this.max)
        },
        libraryActionMode() {
            return this.canReplaceFromLibrary ? 'replace' : 'add'
        },
        openLibraryLabel() {
            if (this.libraryActionMode === 'replace') return this.$t('Replace file')

            return this.libraryAllowsMultiple
                ? this.$t('Select files from media library')
                : this.$t('Select file from media library')
        },
        openLibraryHint() {
            if (this.libraryActionMode === 'replace') {
                return this.$t('Replace the current file with one from the media library.')
            }

            return this.libraryAllowsMultiple
                ? this.$t('Select existing files from the media library or upload new ones below.')
                : this.$t('Select an existing file from the media library or upload a new one below.')
        },
        maxSize() {
            return _.get(this.config, 'media.maxSize') || 12
        },
        settings() {
            let settings = _.get(this.config, 'settings') || []
            settings = settings.filter(setting => setting.field === 'media')
            return settings
        },
        uploadData() {
            let uploadData = {
                'context[type]': 'content'
            }
            return uploadData
        },
        // form() {
        //     return store.getters[`${this.module}/form`]
        // },
        // ui() {
        //     return store.getters[`${this.module}/ui`]
        // },
        uploadEndpoint() {
            return `${this.setupStore.uploadEndpoint}/public/${this.activeCms.id}/content`
        },
        setupStore() {
            return useSetupStore()
        },
        activeCms() {
            return this.setupStore.activeCms
        },
    },
    methods: {
        getValidMedia(content) {
            return (_.get(content, 'media') || []).filter(media => this.isValidMediaObject(media))
        },
        getDisplayMedia(content) {
            return this.getValidMedia(content)[0]
        },
        unlockSection() {
            this.$delete(this.section, 'locked')
        },
        skip(content) {
            return !this.filteredContents.some(el => el.id === content.id)
        },
        isValidMediaObject(media) {
            let isValid = false

            switch (_.get(media, 'type')) {
                case 'file':
                    isValid = !!_.get(media, 'file')
                    break
                case 'image':
                case 'video':
                    isValid = !!_.get(media, `${media.type}.source`)
                    break
            }

            return isValid
        },
        openLibrary(mode = 'add') {
            this.libraryModal.mode = typeof mode === 'string' ? mode : 'add'
            this.libraryModal.visibility = true
        },
        getMediaSourceId(media) {
            switch (_.get(media, 'type')) {
                case 'file':
                    return _.get(media, 'file.id') || _.get(media, 'file.source.id') || null
                case 'image':
                    return _.get(media, 'image.source.id') || _.get(media, 'image.files.0.id') || null
                case 'video':
                    return _.get(media, 'video.source.id') || _.get(media, 'video.files.0.id') || null
                default:
                    return null
            }
        },
        getExistingMedia() {
            if (this.content) {
                return _.get(this.content, 'media', []) || []
            }

            return this.actualFilteredContents.flatMap(content => _.get(content, 'media', []))
        },
        filterDuplicateLibraryFiles(files, existingMedia = []) {
            const seen = new Set(
                existingMedia
                    .map(media => this.getMediaSourceId(media))
                    .filter(value => value !== null && value !== undefined && value !== '')
                    .map(value => String(value))
            )

            return files.filter(file => {
                const key = _.get(file, 'id')
                if (key === null || key === undefined || key === '') return true

                const normalizedKey = String(key)
                if (seen.has(normalizedKey)) return false

                seen.add(normalizedKey)
                return true
            })
        },
        detectLibraryMediaType(file) {
            // media-content handles file/download sections — all files are stored as
            // type='file' so no image/video record creation is needed on the server.
            return 'file'
        },
        buildLibraryFileSource(file) {
            const parsedId = parseInt(_.get(file, 'id'), 10)
            const size = _.get(file, 'size')

            return {
                id: Number.isNaN(parsedId) ? _.get(file, 'id') : parsedId,
                type: 'original',
                name: _.get(file, 'name'),
                preferred_name: _.get(file, 'preferred_name'),
                mime: _.get(file, 'mime'),
                size: size !== undefined && size !== null ? String(size) : '',
            }
        },
        buildMediaFromLibraryFile(file, currentMedia = null, position = 1) {
            const type = this.detectLibraryMediaType(file)
            const source = this.buildLibraryFileSource(file)
            const media = {
                id: uniqid('new:file-library-media:'),
                type,
                scope: _.get(currentMedia, 'scope', 'content'),
                position,
                settings: _.cloneDeep(_.get(currentMedia, 'settings')),
            }

            switch (type) {
                case 'image':
                    media.image = {
                        id: uniqid('new:file-library-image:'),
                        alt: _.get(currentMedia, 'image.alt', ''),
                        source,
                    }
                    break
                case 'video':
                    media.video = {
                        id: uniqid('new:file-library-video:'),
                        source,
                    }
                    break
                default:
                    media.file = source
            }

            return media
        },
        replaceMediaFromLibrary(file) {
            if (this.content) {
                const mediaItems = [...(_.get(this.content, 'media', []) || [])]
                const targetIndex = mediaItems.findIndex(media => !media.deleted)
                if (targetIndex < 0) return

                const originalMedia = mediaItems[targetIndex]
                const replacement = this.buildMediaFromLibraryFile(file, originalMedia, _.get(originalMedia, 'position', 1))
                // Reuse existing media ID so the API updates the record rather than
                // creating a new one alongside the old, which would leave the content
                // with two media entries and break the filteredContents (length === 1) check.
                if (originalMedia.id && !String(originalMedia.id).startsWith('new:')) {
                    replacement.id = originalMedia.id
                }
                mediaItems.splice(targetIndex, 1, replacement)
                this.$set(this.content, 'media', mediaItems)
                this.unlockSection()
                this.$forceUpdate()
                return
            }

            let activeContent = this.actualFilteredContents.find(content => !content.deleted)

            if (!activeContent) {
                // API-loaded content may not pass isValidMediaObject; fall back to key-only lookup
                activeContent = (this.section.contents || []).find(
                    content => content.key === this.config.key && content.media && !content.deleted
                )
            }

            if (!activeContent) return

            const mediaItems = [...(_.get(activeContent, 'media', []) || [])]
            const targetIndex = mediaItems.findIndex(media => !media.deleted)
            if (targetIndex < 0) return

            const originalMedia = mediaItems[targetIndex]
            const replacement = this.buildMediaFromLibraryFile(file, originalMedia, _.get(originalMedia, 'position', 1))
            // Reuse existing media ID so the API updates the record rather than creating
            // a new one, which would leave the content with two media entries.
            if (originalMedia.id && !String(originalMedia.id).startsWith('new:')) {
                replacement.id = originalMedia.id
            }
            mediaItems.splice(targetIndex, 1, replacement)

            const contentIndex = (this.section.contents || []).findIndex(content => String(content.id) === String(activeContent.id))
            if (contentIndex >= 0) {
                this.$set(this.section.contents, contentIndex, {
                    ...activeContent,
                    media: mediaItems,
                })
            }

            this.unlockSection()
            this.$forceUpdate()
        },
        selectMediaFromLibrary(selection) {
            const selectedFiles = Array.isArray(selection) ? selection : [selection]

            if (this.libraryModal.mode === 'replace' && selectedFiles.length) {
                this.replaceMediaFromLibrary(selectedFiles[0])
                this.libraryModal.mode = 'add'
                this.unlockSection()
                return
            }

            const uniqueSelectedFiles = this.filterDuplicateLibraryFiles(selectedFiles, this.getExistingMedia())
            if (!uniqueSelectedFiles.length) {
                this.libraryModal.mode = 'add'
                this.unlockSection()
                return
            }

            if (this.content) {
                if (!this.content.media) this.$set(this.content, 'media', [])

                let position = Math.max(...this.content.media.map(media => _.get(media, 'position', 0) || 0), 0)
                uniqueSelectedFiles.forEach(file => {
                    position += 1
                    this.content.media.push(this.buildMediaFromLibraryFile(file, null, position))
                })

                this.$forceUpdate()
                this.libraryModal.mode = 'add'
                this.unlockSection()
                return
            }

            uniqueSelectedFiles.forEach((file, index) => {
                const content = {
                    key: this.config.key,
                    position: Date.now() + index,
                    media: [this.buildMediaFromLibraryFile(file)],
                }

                addContentToSection(this.section, this.config, content)
            })

            this.$nextTick(() => {
                const contents = _.get(this.section, 'contents') || []
                sortContents(contents)
                this.unlockSection()
            })

            this.libraryModal.mode = 'add'
        },
        getContentIndex(id) {
            // let contents = []
            // if (this.module === 'menu' && this.i >= 0) {
            //     contents = _.get(this.form.menuItems[this.i].page.sections[this.s], 'contents', [])
            // } else if (this.module === 'categoryPage') {
            //     contents = _.get(this.form.sections[this.s], 'contents', [])
            // }

            const contents = _.get(this.section, 'contents') || []
            return _.findIndex(contents, { id })
        },
        deleteContent(id) {
            // const c = this.getContentIndex(id)
            // if (c !== null) {
            //     store.commit(`${this.module}/form/markContentAsDeleted`, { i: this.i, s: this.s, c })
            // }

            if (!this.content) {
                let contents = _.get(this.section, 'contents') || [];
                const i = contents.findIndex(content => content.id === id);
                // contents.splice(i, 1);
                this.$delete(contents, i);
            }
        },
        restoreContent(id) {
            // const c = this.getContentIndex(id)
            // if (c !== null) {
            //     store.commit(`${this.module}/form/unmarkContentAsDeleted`, { i: this.i, s: this.s, c })
            // }
        },
        getMediaIndex(id) {
            // let media = []
            // if (this.module === 'menu' && this.i >= 0) {
            //     media = _.get(this.form.menuItems[this.i].page.sections[this.s].contents[this.c], 'media', [])
            // } else if (this.module === 'categoryPage') {
            //     media = _.get(this.form.sections[this.s].contents[this.c], 'media', [])
            // }

            // return _.findIndex(media, { id })
        },
        deleteMedia(id) {
            // const img = this.getMediaIndex(id)
            // if (img !== null) {
            //     store.commit(`${this.module}/form/markContentMediaAsDeleted`, { i: this.i, s: this.s, c: this.c, img })
            // }

            if (this.content) {
                let contentMedia = _.get(this.content, 'media') || [];
                const i = contentMedia.findIndex(media => media.id === id);
                // contentMedia.splice(i, 1);
                this.$delete(contentMedia, i);
            }
        },
        restoreMedia(id) {
            // const img = this.getMediaIndex(id)
            // if (img !== null) {
            //     store.commit(`${this.module}/form/unmarkContentMediaAsDeleted`, { i: this.i, s: this.s, c: this.c, img })
            // }
        },
        async upoloadChanged(file, fileList) {
            if (fileList.length) this.$set(this.section, 'locked', true)
            return await this.updateUploadHeaders()
        },
        async updateUploadHeaders() {
            // The computed uploadHeaders reads the store token; make sure it is fresh.
            await ensureFreshToken()
        },
        async validateUpload(file) {
            await this.updateUploadHeaders()
            if (file.size / 1024 / 1024 > this.maxSize) {
                this.$notify.error({
                    title: this.$t('upload.sizeExceeded'),
                    message: this.$t('upload.max', { size: `${this.maxSize}mb` }),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                })
                // Reject, never `return false`: an async before-upload always returns a promise,
                // and Element aborts only on rejection - a resolved `false` uploads anyway.
                return Promise.reject()
            } else {
                this.$set(this.section, 'locked', true)
                return true
            }
        },
        async uploadSucceeded(response, { uid }) {
            let source = _.cloneDeep(response)

            source.upload = true
            source.type = 'original'

            delete source.hash
            delete source.dimensions
            if ( typeof source.size === 'number' ) source.size = source.size.toString()
            let type, file, image, video

            // detect by mime what kind (type)
            // of media has been uploaded

            const suffix = mime.getExtension(response.mime)
            switch (suffix) {
                case 'jpg':
                case 'jpeg':
                case 'png':
                    type = 'image'
                    image = {
                        id: uniqid('new:'),
                        width: _.get(response, 'dimensions.width'),
                        height: _.get(response, 'dimensions.height'),
                        source
                    }
                    break;

                case 'mp4':
                case 'mpeg':
                case 'webm':
                case 'qt':
                    type = 'video'
                    video = {
                        id: uniqid('new:'),
                        width: _.get(response, 'dimensions.width'),
                        height: _.get(response, 'dimensions.height'),
                        source
                    }
                    break;

                default:
                    type = 'file'
                    file = source
            }

            // const storageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`, '/')
            // const table = 'uploads'

            // const token = AuthApi.getToken()

            // let img = new Image()
            // img.src = `${storageRoute}/${table}/${image.id}/${image.name}?token=${token}`
            // img.onload = () => {
            //     image.width = img.width
            //     image.height = img.height

            //     const payload = {
            //         i: this.i,
            //         p: this.p,
            //         s: this.s,
            //         config: this.config,
            //         content: {
            //             key: this.config.key,
            //             media: [ image ],
            //         }
            //     }
            //     if(this.c !== false && this.c >= 0 ) {
            //         store.commit(`${this.module}/form/addImageToContent`, {
            //             i: this.i,
            //             p: this.p,
            //             s: this.s,
            //             c: this.c,
            //             image
            //         })
            //     } else store.commit(`${this.module}/form/addContent`, payload)
            // }

            let media = {
                id: uniqid('new:'),
                type, // switch/case from above
                scope: 'content',
                position: 1,

                file,
                image,
                video,

                settings: undefined,
            }

            // const payload = {
            //     i: this.i,
            //     p: this.p,
            //     s: this.s,
            //     config: this.config,
            //     content: {
            //         key: this.config.key,
            //         // position: _.get(this.section, 'contents.length', 0) + 1,
            //         position: uid,
            //         media: [ media ],
            //     }
            // }

            if (this.content) {
                // store.commit(`${this.module}/form/addImageToContent`, {
                //     i: this.i,
                //     p: this.p,
                //     s: this.s,
                //     c: this.c,
                //     image
                // })

                if (!this.content.media) this.$set(this.content, 'media', []);
                const length = this.content.media.length;
                this.$set(this.content.media, length, media);
            } else {
                // store.commit(`${this.module}/form/addContent`, payload)
                const content = {
                    key: this.config.key,
                    // position: _.get(this.section, 'contents.length', 0) + 1,
                    position: uid,
                    media: [ media ],
                }
                const section = addContentToSection(this.section, this.config, content)
            }

            this.$nextTick(() => {
                let contents = _.get(this.section, 'contents') || [];
                if (this.$refs.upload !== undefined) {
                    this.$refs.upload.uploadFiles = this.$refs.upload.uploadFiles.filter(el => el.uid !== uid)
                    if (! this.$refs.upload.uploadFiles.length) {
                        // store.commit(`${this.module}/sortContents`, { i: this.i, s: this.s, contents: this.contents })
                        sortContents(contents)
                        this.$delete(this.section, 'locked', false)
                    }
                } else {
                    // store.commit(`${this.module}/sortContents`, { i: this.i, s: this.s, contents: this.contents })
                    sortContents(contents)
                    this.$delete(this.section, 'locked', false)
                }
            })

            return await this.updateUploadHeaders()
        },
        async uploadFailed(err, file) {
            await this.updateUploadHeaders()

            this.$notify.error({
                title: this.$t('upload.notUploaded'),
                message: _.get(file, 'name'),
                iconClass: 'fas el-icon-fa-xmark',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            })
        },
    },
    mounted() {
        // const calculateMaxWidth = () => {
        //     if (this.$refs.container) {
        //         const style = getComputedStyle(this.$refs.container)
        //         this.maxWidth = this.$refs.container.clientWidth
        //         this.maxWidth -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)
        //     }
        // }

        // window.addEventListener('resize', _.debounce(calculateMaxWidth, 100))
        // calculateMaxWidth()
    }
}
</script>

<style lang="sass" scoped>
.no-style-list
    margin-bottom: 4px

.drag-hint
    font-size: 12px
    color: #c0c4cc
    margin-bottom: 12px

.content
    position: relative

.media-actions
    margin-bottom: 10px
    padding: 12px
    border: 1px solid #dce6f8
    border-radius: 8px
    background: #f5f8ff
    &--replace
        display: flex
        justify-content: flex-start
        padding: 0
        border: 0
        border-radius: 0
        background: transparent

.media-library-button
    width: 100%
    min-height: 44px
    font-weight: 600
    &::v-deep span
        white-space: normal
    &--replace
        width: auto
        min-height: 0
        font-weight: 500

.media-actions__hint
    margin-top: 8px
    font-size: 12px
    line-height: 1.45
    color: #6b7280

.media-sort-hint
    display: inline-flex
    align-items: center
    gap: 6px
    margin: 2px 0 8px
    font-size: 12px
    line-height: 1.4
    color: #6b7280

.el-button.delete-media,
.el-button.restore-media
    position: absolute
    right: 5px
    top: 50%
    transform: translateY(-50%)
</style>
